/**
 * InboundDocumentCard — one inbound document as a clickable summary row
 * (WL-A6, polish pass posteingang-polish). Shows the §10 badges, sender,
 * document number/date, the receipt date (GoBD trust signal) and the gross
 * amount as the visual hero. NO delete affordance (archive is immutable).
 * Click → detail view.
 *
 * Colour note: this component uses the `indigo-*` Tailwind utility classes
 * throughout. `tailwind.config.js` globally overrides the `indigo` palette
 * with the FaeBill brand Mint ramp (indigo-500 == #20D08A) — these classes
 * already render as Mint, not literal indigo/purple (see changelog).
 *
 * @module InboundDocumentCard
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Timestamp } from 'firebase/firestore';
import { IconChevronRight } from '../../icons';
import type { InboundDocument } from '../../types/inboundTypes';
import { deriveInboundBadges } from '../../utils/inboundAdapters';
import { formatDate as formatDateCentral } from '../../utils/formatters';
import InboundBadgeList from './InboundBadgeList';

interface InboundDocumentCardProps {
  doc: InboundDocument;
  onClick: (doc: InboundDocument) => void;
}

/** Formats decimal-euro as a locale currency string; empty for 0/undefined. */
function formatAmount(value: number | undefined, currency: string | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) return '';
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency && currency.length === 3 ? currency : 'EUR',
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency ?? 'EUR'}`;
  }
}

/** ISO date (YYYY-MM-DD) → locale date (zentrales formatDate, M-G); empty string when absent. */
function formatDate(value: string | null | undefined, locale?: string): string {
  if (!value) return '';
  return formatDateCentral(value, { locale });
}

/** Firestore `Timestamp` → locale date; empty string when absent/invalid. */
function formatReceivedAt(value: Timestamp | null | undefined, locale?: string): string {
  if (!(value instanceof Timestamp)) return '';
  try {
    return formatDateCentral(value.toDate(), { locale });
  } catch {
    return '';
  }
}

/**
 * Derives a 1-2 letter uppercase initials chip from a sender string (name or
 * email). Purely decorative/scannability — no i18n needed, no semantic value
 * beyond a visual anchor.
 */
function getInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '';
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

const InboundDocumentCard: React.FC<InboundDocumentCardProps> = ({ doc, onClick }) => {
  const { t, i18n } = useTranslation('posteingang');
  const badges = deriveInboundBadges(doc);

  const sender =
    doc.extracted?.sellerName ||
    doc.mail?.from ||
    doc.filename ||
    t('unknownSender', 'Unbekannter Absender');
  const number = doc.extracted?.invoiceNumber || '';
  const dateStr = formatDate(doc.extracted?.documentDate, i18n.language);
  const receivedStr = formatReceivedAt(doc.receivedAt, i18n.language);
  const amount = formatAmount(doc.extracted?.grossTotal, doc.extracted?.currency);
  const initials = getInitials(sender);

  return (
    <button
      type="button"
      onClick={() => onClick(doc)}
      className="text-left bg-surface-raised border border-hairline hover:border-indigo-500 rounded-xl p-4 transition-colors w-full flex flex-col gap-2"
    >
      <div className="flex items-start gap-3">
        {/* Initials chip — scannability anchor, purely decorative. */}
        {initials && (
          <div
            aria-hidden="true"
            className="shrink-0 h-9 w-9 rounded-full bg-[var(--tone-accent-bg)] border border-[var(--tone-accent-border)] text-[var(--tone-accent-fg)] flex items-center justify-center text-xs font-semibold"
          >
            {initials}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <span className="text-primary font-medium truncate">{sender}</span>
            <IconChevronRight className="text-faint flex-shrink-0 mt-1" />
          </div>
          <div className="mt-1">
            <InboundBadgeList badges={badges} />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-2 mt-1">
        {/* Quiet secondary metadata: invoice number, document date, and the
            receipt date (GoBD trust signal, WP §7) — each clearly labelled so
            "Rechnungsdatum" and "Empfangen am" cannot be confused. */}
        <div className="text-sm text-muted min-w-0">
          {number && <p className="truncate">{number}</p>}
          {dateStr && (
            <p className="truncate">
              {t('card.documentDate', 'Rechnungsdatum: {{date}}', { date: dateStr })}
            </p>
          )}
          {receivedStr && (
            <p className="truncate text-faint">
              {t('card.receivedAt', 'Empfangen am {{date}}', { date: receivedStr })}
            </p>
          )}
        </div>

        {/* Amount as the visual hero — larger, bold, brand-coloured. */}
        {amount && (
          <span className="text-lg font-semibold text-accent-ink tabular-nums whitespace-nowrap">
            {amount}
          </span>
        )}
      </div>
    </button>
  );
};

export default InboundDocumentCard;
